import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Separator } from '@neo/primitives/separator'
import { useReactFlow } from '@xyflow/react'
import { Plus, X } from 'lucide-react'
import type { ElementType } from 'react'
import z from 'zod'
import { AudioPrompt } from '../components/audio-prompt'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { IVRKeypad } from '../components/ivr-dialer'
import { defaultIVRNodeData, outputStates } from '../config/nodes.config'
import { v2validations } from '../config/validation.config'
import type { IVRNodeData, OutputState, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = IVRNodeData['config']

// Node-level event callbacks for IVR menu (legacy PHLO). SER-4772.
const ivrMenuEventTypes = [{ value: 'digits', label: 'Digits' }]

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).ivr_menu.shape
  return [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      zodValidation: validations.name,
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'allowed_inputs',
      type: 'custom',
      component: IVRKeypad as ElementType<CompProps<FormValues>>,
      label: 'Select IVR Inputs',
      zodValidation: validations.allowed_inputs,
      eventDataExtractor: (data) => data as string[],
    },
    {
      name: 'input_timeout',
      componentType: 'inputNumber',
      label: 'Input Timeout',
      zodValidation: validations.input_timeout,
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
        min: 1,
      },
    },
    {
      name: 'interrupt',
      componentType: 'checkbox',
      label: 'Interrupt audio on input',
      zodValidation: validations.interrupt,
      className: '-mt-1',
    },
    {
      name: 'digit_timeout',
      componentType: 'inputNumber',
      label: 'Digit Timeout',
      zodValidation: validations.digit_timeout,
      inputProps: {
        type: 'number',
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
        min: 1,
      },
    },
    {
      name: 'num_digits',
      componentType: 'inputNumber',
      label: 'Number of Digits',
      zodValidation: validations.num_digits,
      inputProps: {
        type: 'number',
        min: 1,
      },
    },
    {
      name: 'prompt',
      type: 'array',
      className: 'relative',
      zodValidation: z.array(z.any()).min(1, 'At least one prompt is required'), // A UI issue would occur if we use the zodValidation from the schema
      defaultNewValue: defaultIVRNodeData.config.prompt[0] as unknown as Record<string, unknown>,
      removeItemButton: <X className="w-4 h-4 absolute top-[16px] right-0 text-tertiary" />,
      addItemButton: (props) => (
        <div>
          <Button variant="ghost" className="form-button" {...props}>
            <Plus className="w-4 h-4 mr-2" />
            Add Prompt
          </Button>
        </div>
      ),
      subFields: [
        {
          name: 'separator',
          type: 'slot',
          element: () => <Separator orientation="horizontal" className="h-px w-full mb-4 bg-bg1" />,
        },
        {
          name: 'prompt[0].delay',
          componentType: 'inputNumber',
          className: 'mt-2 mb-2',
          zodValidation: validations.prompt.element.innerType().shape.delay,
          getDynamicProps: (_, i) => ({
            inputProps: {
              type: 'number',
              label: `Wait time before Prompt ${(i || 0) + 1}`,
              endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
              min: 0,
            },
          }),
        },
        {
          name: '',
          type: 'sub-item',
          component: (props) => (
            // @ts-expect-error - Deepkeys won't support this
            <AudioPrompt {...props} nodeVars={nodeVars} />
          ),
          zodValidation: validations.prompt.element,
          eventDataExtractor: (data) => data as Record<string, unknown>,
        },
      ],
    },
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={ivrMenuEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

export const IVRSidebarComponent = (props: SidebarComponentProps<IVRNodeData>) => {
  const { setEdges } = useReactFlow()
  const { selectedNode, nodeVars } = props
  const formFields = fields(nodeVars)
  const ivrMenuOutputStates = outputStates.ivr_menu as OutputState[]

  return (
    <GenericSidebarComponent
      fields={formFields}
      {...props}
      callback={(data) => {
        // Filter out any edges that might be removed due to the new allowed_inputs
        setEdges((edges) =>
          edges.filter(
            (edge) =>
              edge.source !== selectedNode.id ||
              data.allowed_inputs.includes(edge.sourceHandle || '') ||
              edge.sourceHandle === ivrMenuOutputStates[0].id || // no-input port
              edge.sourceHandle === ivrMenuOutputStates[1].id, // wrong-input port
          ),
        )
      }}
    />
  )
}
